import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Processor } from '../../../Processing/components/hooks/useProcessing';

export interface ProcessorNode {
    id: string;
    processor: Processor;
    position: { x: number; y: number };
    arguments: { [key: string]: string };
    saveOutput: boolean;
}

export interface ProcessingFlowConnection {
    id: string;
    sourceId: string;
    targetId: string;
}

interface ProcessingFlowState {
    nodes: ProcessorNode[];
    connections: ProcessingFlowConnection[];
    selectedNodeId?: string;
    canvasPosition: { x: number; y: number };
    canvasZoom: number;
    isDragging: boolean;
    draggedNodeType?: string;
    dragHoverPosition?: { x: number; y: number };
    // Store legacy processing flows separately for compatibility
    legacyFlows: { [flowId: string]: any };
}

const initialState: ProcessingFlowState = {
    nodes: [],
    connections: [],
    selectedNodeId: undefined,
    canvasPosition: { x: 0, y: 0 },
    canvasZoom: 1,
    isDragging: false,
    draggedNodeType: undefined,
    dragHoverPosition: undefined,
    legacyFlows: {},
};

const processingFlowSlice = createSlice({
    name: 'processingFlow',
    initialState,
    reducers: {
        addProcessorNode: (
            state,
            action: PayloadAction<{
                processor: Processor;
                position: { x: number; y: number };
            }>,
        ) => {
            const { processor, position } = action.payload;
            const newNode: ProcessorNode = {
                id: `node-${Date.now()}`,
                processor,
                position,
                arguments: (() => {
                    // Cast all arguments to strings as required by schema
                    const stringArguments: { [key: string]: string } = {};
                    if (
                        processor.arguments &&
                        typeof processor.arguments === 'object'
                    ) {
                        Object.keys(processor.arguments).forEach((key) => {
                            stringArguments[key] = String(
                                processor.arguments[key],
                            );
                        });
                    }
                    return stringArguments;
                })(),
                saveOutput: true, // Always true for local processing flows
            };

            // Auto-connect to the last node in the flow if there are existing nodes
            if (state.nodes.length > 0) {
                const lastNode = state.nodes[state.nodes.length - 1];
                const newConnection: ProcessingFlowConnection = {
                    id: `conn-${lastNode.id}-${newNode.id}`,
                    sourceId: lastNode.id,
                    targetId: newNode.id,
                };
                state.connections.push(newConnection);
            }

            state.nodes.push(newNode);
        },
        updateProcessorNode: (
            state,
            action: PayloadAction<{
                nodeId: string;
                updates: Partial<ProcessorNode>;
            }>,
        ) => {
            const { nodeId, updates } = action.payload;
            const nodeIndex = state.nodes.findIndex(
                (node) => node.id === nodeId,
            );
            if (nodeIndex !== -1) {
                state.nodes[nodeIndex] = {
                    ...state.nodes[nodeIndex],
                    ...updates,
                };
            }
        },
        removeProcessorNode: (state, action: PayloadAction<string>) => {
            const nodeId = action.payload;
            state.nodes = state.nodes.filter((node) => node.id !== nodeId);
            // Remove connections involving this node
            state.connections = state.connections.filter(
                (conn) => conn.sourceId !== nodeId && conn.targetId !== nodeId,
            );
            // Clear selection if this node was selected
            if (state.selectedNodeId === nodeId) {
                state.selectedNodeId = undefined;
            }
        },
        moveProcessorNode: (
            state,
            action: PayloadAction<{
                nodeId: string;
                position: { x: number; y: number };
            }>,
        ) => {
            const { nodeId, position } = action.payload;
            const nodeIndex = state.nodes.findIndex(
                (node) => node.id === nodeId,
            );
            if (nodeIndex !== -1) {
                state.nodes[nodeIndex].position = position;
            }
        },
        reorderProcessorNodes: (
            state,
            action: PayloadAction<{
                fromIndex: number;
                toIndex: number;
            }>,
        ) => {
            const { fromIndex, toIndex } = action.payload;
            if (
                fromIndex >= 0 &&
                fromIndex < state.nodes.length &&
                toIndex >= 0 &&
                toIndex < state.nodes.length &&
                fromIndex !== toIndex
            ) {
                // Remove the node from its current position
                const [movedNode] = state.nodes.splice(fromIndex, 1);
                // Insert it at the new position
                state.nodes.splice(toIndex, 0, movedNode);

                // Rebuild connections based on new array order
                state.connections = [];
                for (let i = 0; i < state.nodes.length - 1; i++) {
                    state.connections.push({
                        id: `conn-${i}-${i + 1}`,
                        sourceId: state.nodes[i].id,
                        targetId: state.nodes[i + 1].id,
                    });
                }
            }
        },
        redrawFlowLayout: (state) => {
            // Redraw nodes in sequential order on canvas
            const NODE_WIDTH = 200; // Standard node width
            const NODE_SPACING = 50; // 50px spacing between nodes
            const HORIZONTAL_SPACING = NODE_WIDTH + NODE_SPACING; // 250px total spacing
            const VERTICAL_CENTER = 300;

            state.nodes.forEach((node, index) => {
                node.position = {
                    x: 100 + index * HORIZONTAL_SPACING,
                    y: VERTICAL_CENTER,
                };
            });

            // Rebuild connections to ensure they're in order
            state.connections = [];
            for (let i = 0; i < state.nodes.length - 1; i++) {
                state.connections.push({
                    id: `conn-${i}-${i + 1}`,
                    sourceId: state.nodes[i].id,
                    targetId: state.nodes[i + 1].id,
                });
            }
        },
        selectProcessorNode: (
            state,
            action: PayloadAction<string | undefined>,
        ) => {
            state.selectedNodeId = action.payload;
        },
        addConnection: (
            state,
            action: PayloadAction<{
                sourceId: string;
                targetId: string;
            }>,
        ) => {
            const { sourceId, targetId } = action.payload;
            const newConnection: ProcessingFlowConnection = {
                id: `conn-${Date.now()}`,
                sourceId,
                targetId,
            };
            state.connections.push(newConnection);
        },
        removeConnection: (state, action: PayloadAction<string>) => {
            const connectionId = action.payload;
            state.connections = state.connections.filter(
                (conn) => conn.id !== connectionId,
            );
        },
        setCanvasPosition: (
            state,
            action: PayloadAction<{ x: number; y: number }>,
        ) => {
            state.canvasPosition = action.payload;
        },
        setCanvasZoom: (state, action: PayloadAction<number>) => {
            state.canvasZoom = action.payload;
        },
        setDragState: (
            state,
            action: PayloadAction<{
                isDragging: boolean;
                draggedNodeType?: string;
                dragHoverPosition?: { x: number; y: number };
            }>,
        ) => {
            const { isDragging, draggedNodeType, dragHoverPosition } =
                action.payload;
            state.isDragging = isDragging;
            state.draggedNodeType = draggedNodeType;
            state.dragHoverPosition = dragHoverPosition;
        },
        loadProcessingFlow: (
            state,
            action: PayloadAction<{
                nodes: ProcessorNode[];
                connections: ProcessingFlowConnection[];
            }>,
        ) => {
            const { nodes, connections } = action.payload;
            // Ensure all nodes have saveOutput set to true for local flows
            state.nodes = nodes.map((node) => ({
                ...node,
                saveOutput: true,
            }));
            state.connections = connections;
            state.selectedNodeId = undefined;
        },
        resetProcessingFlow: (state) => {
            state.nodes = [];
            state.connections = [];
            state.selectedNodeId = undefined;
            state.canvasPosition = { x: 0, y: 0 };
            state.canvasZoom = 1;
            // Don't clear legacyFlows - they should persist across editor sessions
        },
        clearAllFlows: (state) => {
            state.nodes = [];
            state.connections = [];
            state.selectedNodeId = undefined;
            state.canvasPosition = { x: 0, y: 0 };
            state.canvasZoom = 1;
            state.legacyFlows = {};
        },
        addProcessingFlowFromDefinition: (
            state,
            action: PayloadAction<{
                id: string;
                flow: any; // Legacy processing flow format
            }>,
        ) => {
            const { id, flow } = action.payload;
            // Store legacy flows separately for compatibility
            state.legacyFlows[id] = flow;
            console.log('Legacy processing flow loaded:', { id, flow });
        },
        updateLegacyFlow: (
            state,
            action: PayloadAction<{
                id: string;
                flowDefinition: any[]; // Array of processors in legacy format
            }>,
        ) => {
            const { id, flowDefinition } = action.payload;
            // Update the legacy flow with new definition
            state.legacyFlows[id] = flowDefinition;
            console.log('Legacy processing flow updated:', {
                id,
                flowDefinition,
            });
        },
    },
});

export const {
    addProcessorNode,
    updateProcessorNode,
    removeProcessorNode,
    moveProcessorNode,
    reorderProcessorNodes,
    redrawFlowLayout,
    selectProcessorNode,
    addConnection,
    removeConnection,
    setCanvasPosition,
    setCanvasZoom,
    setDragState,
    loadProcessingFlow,
    resetProcessingFlow,
    clearAllFlows,
    addProcessingFlowFromDefinition,
    updateLegacyFlow,
} = processingFlowSlice.actions;

export default processingFlowSlice.reducer;
